import React, { useState } from 'react';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import { PrimaryButton, TertiaryButton } from 'v3/Components/Shared/Buttons';
import { buildEndpoint, useDataPoster } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { useToasts } from 'Context';
import cloudyIcon from 'Assets/Icons/cloudy.svg';

const MAX_NOTE_LENGTH = 5000;

export type InternalNoteData = {
  note?: string | null;
};

type Props = {
  data: InternalNoteData;
  reviewerId?: string;
  onCancel: () => void;
  onSaveSuccess: () => void;
};

type ReadOnlyProps = { data: InternalNoteData };

export const InternalNoteReadOnly: React.FC<ReadOnlyProps> = ({ data }) => {
  if (!data.note) {
    return (
      <div className="overview-internal-note__empty">
        <img src={cloudyIcon} alt="" className="overview-internal-note__straddie" />
        <p className="overview-internal-note__empty-text">Add a note here</p>
      </div>
    );
  }

  return <p className="overview-internal-note__text">{data.note}</p>;
};

export const InternalNoteActive: React.FC<Props> = ({ data, reviewerId, onCancel, onSaveSuccess }) => {
  const [note, setNote] = useState(data.note ?? '');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToasts();

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'internalNote'),
    undefined,
    true,
    '3.0'
  );

  const isDirty = note !== (data.note ?? '');

  const handleCancel = () => {
    if (isDirty) {
      setConfirmDiscard(true);
    } else {
      onCancel();
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const resp: any = await postData({ note: note.trim() || null });
    setIsSaving(false);
    if (resp?.status === 200) {
      toast.success('Internal note has been updated');
      onSaveSuccess();
    } else {
      toast.error('Failed to save internal note. Please try again.');
    }
  };

  return (
    <div>
      <div className="overview-internal-note__field">
        <label className="overview-internal-note__label">Assigning Note</label>
        <textarea
          className="overview-internal-note__textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={MAX_NOTE_LENGTH}
          rows={8}
        />
        <p className="overview-internal-note__hint">
          This note will appear in the assigning flow for internal users to reference
        </p>
      </div>

      <div className="overview-rates-actions">
        <TertiaryButton onClick={handleCancel}>Cancel</TertiaryButton>
        <PrimaryButton onClick={handleSave} disabled={isSaving}>Save</PrimaryButton>
      </div>

      <ConfirmDialog
        dialogVisible={confirmDiscard}
        toggleDialog={() => setConfirmDiscard(false)}
        title="Discard unsaved changes?"
        primaryButtonText="Discard"
        secondaryButtonText="Keep Editing"
        OnPrimaryButtonClick={() => { setConfirmDiscard(false); onCancel(); }}
        OnSecondaryButtonClick={() => setConfirmDiscard(false)}
        isPrimaryDanger
      />
    </div>
  );
};
